/**
 * The round's two fixed overlays on desktop (migration 3039):
 * design/GT Round.dc.html `part="modals"` — the hover panel that names the
 * slice under the cursor, and the one-shot join card that fires bottom-right
 * of the board when someone starts a round. Mounted once from `DesktopApp`,
 * beside the other modals, so no transformed hover tile can clip them.
 */

import { Avatar } from '../../components/Avatar.tsx';
import { CoverArt } from '../../components/CoverArt.tsx';
import { PixelButton } from '../../components/PixelButton.tsx';
import { NominatorStack } from '../../components/Wheel.tsx';
import { useArt } from '../../components/useArt.ts';
import { useQd } from '../../store/index.ts';
import { roundChipVM, roundVM } from '../../store/selectors.round.ts';
import type { RoundVM } from '../../store/selectors.round.ts';

const MONO = 'var(--gt-font-mono)';

function HoverPanel({ v }: { v: RoundVM }): JSX.Element {
  const art = useArt(v.hovId + 'p', v.hovCands);
  const W = typeof window === 'undefined' ? 1280 : window.innerWidth;
  const H = typeof window === 'undefined' ? 800 : window.innerHeight;
  const x = Math.min(v.hovX + 18, Math.max(8, W - 306));
  const y = Math.min(v.hovY + 18, Math.max(8, H - 156));
  return (
    <div
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 80,
        pointerEvents: 'none',
        display: 'flex',
        gap: 11,
        width: 288,
        boxSizing: 'border-box',
        background: 'var(--gt-card)',
        border: '1px solid var(--gt-border)',
        boxShadow: 'var(--gt-shadow-toast)',
        padding: 11,
      }}
    >
      <div style={{ position: 'relative', width: 84, flex: 'none', aspectRatio: '2/3', border: '1px solid var(--gt-line)', background: v.hovFall }}>
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={15} ini={v.hovIni} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center', minWidth: 0, flex: 1 }}>
        <span style={{ fontFamily: 'var(--gt-font-sans)', fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: 'var(--gt-text)', textWrap: 'pretty' }}>{v.hovNm}</span>
        {/* every backer, not just the first (3054) — the stack is the reason
            the wedge under the cursor is as wide as it is */}
        {/* The name line WRAPS here, unlike everywhere else it appears: three
            faces plus `YOU, CHRIS & 1 MORE` is a few pixels over a 288px
            panel's text column, and the panel is free to grow downwards. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <NominatorStack bys={v.hovBys} size={22} fs={7} />
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', lineHeight: 1.5, color: 'var(--gt-text-4)', minWidth: 0 }}>{v.hovByTxt}</span>
        </div>
      </div>
    </div>
  );
}

export function RoundHover(): JSX.Element | null {
  const s = useQd();
  if (s.screen !== 'round' || !s.rHov) return null;
  const v = roundVM(s, s.serverNow());
  if (!v || !v.hovOn) return null;
  return <HoverPanel v={v} />;
}

export function RoundJoinCard(): JSX.Element | null {
  const s = useQd();
  const c = roundChipVM(s);
  if (!c.rCardOn) return null;
  return (
    <div
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        zIndex: 70,
        width: 330,
        background: 'var(--gt-card)',
        border: '1px solid var(--acc)',
        boxShadow: 'var(--gt-shadow-toast)',
        animation: 'gt-toast .18s ease-out',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 15px', borderBottom: '1px solid var(--gt-line)' }}>
        <Avatar size={26} fs={8} ini={c.cardWhoIni} bg={c.cardWhoBg} hasAv={c.cardWhoHasAv} avBg={c.cardWhoAv} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0, flex: 1 }}>
          <span style={{ fontFamily: 'var(--gt-font-pixel)', fontSize: 8, color: 'var(--acc)', transform: 'translateY(0.13em)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {c.rCardWho} STARTED GAME NIGHT
          </span>
          <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', whiteSpace: 'nowrap' }}>{c.rCardSub}</span>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 15px' }}>
        <div onClick={() => s.rDismiss()} className="d-hov-text4" style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.06em', color: 'var(--gt-muted)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          NOT TONIGHT
        </div>
        <div style={{ flex: 1 }} />
        <PixelButton label="JOIN ▸" fs={9} pad="13px 18px" nowrap fit lh="1" onClick={() => void s.rJoin()} />
      </div>
    </div>
  );
}
